import React, { useEffect, useState } from 'react';
import { UserModel, UserStatusEnum } from '../../../../interfaces/user.interface';
import { BookModel } from '../../../../interfaces/book.interface';
import { convertTimestampToDateTime } from '../../../../helper/helper';
import Loader from '../../../../components/Common/Loader';
import {
  AdminBooksTab,
  AdminCharactersTab,
  AdminDetailsHeader,
  AdminSummaryCard,
  AdminTabs,
  AdminTabDef,
  SummaryField,
} from '../../../../components/Admin';
import { getAdminUserBooks } from '../../../../services/AdminUser.service';

type TabType = 'books' | 'characters';

interface StudentDetailsViewProps {
  student: UserModel;
  onClose: () => void;
}

const StudentDetailsView: React.FC<StudentDetailsViewProps> = ({ student, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('books');
  const [charactersRefreshKey, setCharactersRefreshKey] = useState(0);

  const [books, setBooks] = useState<BookModel[]>([]);
  const [booksLoader, setBooksLoader] = useState(false);
  const [booksPageNumber, setBooksPageNumber] = useState(1);
  const [totalBooksPages, setTotalBooksPages] = useState<number>(0);

  const fetchBooks = async () => {
    if (!student?.email) return;
    try {
      setBooksLoader(true);
      const { books: fetchedBooks, totalBooksPages: pages } = await getAdminUserBooks(
        student.email,
        booksPageNumber,
        true,
      );
      setBooks(fetchedBooks);
      setTotalBooksPages(pages);
    } finally {
      setBooksLoader(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'books' && student?.email) fetchBooks();
  }, [activeTab, booksPageNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBooksPageChange = (page: number | string) => {
    const p = +page;
    if (p > 0 && p <= totalBooksPages) setBooksPageNumber(p);
  };

  const tabDef: AdminTabDef<TabType>[] = [
    { key: 'books', label: 'Books' },
    { key: 'characters', label: 'Characters' },
  ];

  const handleRefresh = () => {
    if (activeTab === 'books') { setBooks([]); fetchBooks(); }
    else if (activeTab === 'characters') setCharactersRefreshKey((k) => k + 1);
  };

  const bookBalance = +(student?.booksCreateLimit || 0) - +(student?.booksCreatedCounter || 0);
  const statusColor = student.status === UserStatusEnum.ACTIVE ? 'text-green-600' : 'text-red-500';
  const displayName = student.studentName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || '—';

  return (
    <div className="max-w-5xl mx-auto min-h-screen">
      <AdminDetailsHeader
        title={`Student Details — ${displayName}`}
        onClose={onClose}
        onRefresh={handleRefresh}
        refreshing={booksLoader}
      />

      <AdminSummaryCard>
          <SummaryField label="Name" value={displayName} />
          <SummaryField label="Login Name" value={student.loginName} />
          <SummaryField
            label="Email"
            value={
              student.generatedEmail ? (
                <span className="italic text-gray-500">{student.email} (auto)</span>
              ) : (
                student.email
              )
            }
          />
          <SummaryField label="Teacher" value={student.teacherEmail} />
          <SummaryField label="Classroom" value={student.classroomId} />
          <SummaryField label="Book Balance" value={bookBalance} />
          <SummaryField
            label="Status"
            value={
              <span className={`font-semibold capitalize ${statusColor}`}>{student.status || 'active'}</span>
            }
          />
          <SummaryField
            label="Signed Up"
            value={student.timestamp ? convertTimestampToDateTime(student.timestamp) : '—'}
          />
          <SummaryField
            label="Last Session"
            value={student.lastSessionAt ? convertTimestampToDateTime(student.lastSessionAt) : '—'}
          />
      </AdminSummaryCard>

      <AdminTabs<TabType> tabs={tabDef} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'books' && (
        <div>
          {booksLoader ? (
            <div className="flex justify-center py-8">
              <Loader loader={booksLoader} dimentions={'w-8 h-8'} />
            </div>
          ) : (
            <AdminBooksTab
              books={books}
              currentPage={booksPageNumber}
              totalBooksPages={totalBooksPages}
              handleBooksPageChange={handleBooksPageChange}
            />
          )}
        </div>
      )}

      {activeTab === 'characters' && (
        <AdminCharactersTab email={student?.email} active={activeTab === 'characters'} refreshKey={charactersRefreshKey} />
      )}
    </div>
  );
};

export default StudentDetailsView;
